"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useIntersectionObserver = void 0;
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = require("react");
const _excluded = ["useOnce"];
/*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
/**
 * Returns a boolean to indicate if an element is visible on the screen. Fall back to `true`
 * if IntersectionObserver is not supported.
 * https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/IntersectionObserver
 * @param {Object} ref - element ref
 * @param {Object} options
 * @param {Object} options.root - See IntersectionObserver options
 * @param {string} options.rootMargin - See IntersectionObserver options
 * @param {number|Array<number>} options.threshold - See IntersectionObserver options
 * @param {boolean} options.useOnce - Detach the observer after the element appears.
 * @returns {boolean}
 */
const useIntersectionObserver = (ref, options = {}) => {
  const [isIntersecting, setIntersecting] = (0, _react.useState)(false);
  const {
      useOnce
    } = options,
    ioOptions = (0, _objectWithoutProperties2.default)(options, _excluded);
  (0, _react.useEffect)(() => {
    if (!(ref !== null && ref !== void 0 && ref.current)) return;

    // Just set `isIntersecting` true if browser doesn't implement IntersectionObserver. If the use-case
    // is critical and you need to support very old browsers, a polyfill will need to be added.
    if (!('IntersectionObserver' in window) || !('IntersectionObserverEntry' in window) || !('intersectionRatio' in window.IntersectionObserverEntry.prototype)) {
      if (!isIntersecting) {
        setIntersecting(true);
      }

      // We want to return early, but `useEffect` expects a function as the return value,
      // so we just return a noop function.
      return () => null;
    }
    const observer = new IntersectionObserver(([entry]) => {
      const onScreen = entry.isIntersecting;
      setIntersecting(onScreen);
      if (useOnce && onScreen) {
        observer.disconnect();
      }
    }, ioOptions);
    observer.observe(ref === null || ref === void 0 ? void 0 : ref.current);

    // Remove the observer as soon as the component is unmounted
    return () => {
      observer.disconnect();
    };
  }, [ref === null || ref === void 0 ? void 0 : ref.current]);
  return isIntersecting;
};
exports.useIntersectionObserver = useIntersectionObserver;